import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import StepTrace from "./tools/StepTrace";
import ChoicePrompt from "./prompts/ChoicePrompt";
import InputPrompt from "./prompts/InputPrompt";
import AJEProposal from "./proposals/AJEProposal";
import MappingProposal from "./proposals/MappingProposal";
import ContextChangeProposal from "./proposals/ContextChangeProposal";
import SearchableSelect from "./prompts/SearchableSelect";
import { getStepStatusConfig, getStepStatusClass } from "./tools/StepStatusIcon";
import { resolveStepsWithUserResponses } from "./cascades";
import WComponent from "../../../../../../common/WComponent";

// ─── Execution Step Rendering ────────────────────────────────────────────────
//
// Design: completed steps collapse into a quiet summary. Blocked/waiting steps
// are the visual focus with clear "what's needed" language. No internal metadata
// labels (mode, tool) shown to users.

class ExecutionSteps extends WComponent {
	storageKey() {
		return "exec_steps_" + (this.props.clientId || "0") + "_" + (this.props.checkId || "unknown");
	}

	state = {
		showCompleted: false,
		userResponses: (() => {
			try { let saved = localStorage.getItem(this.storageKey()); return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; }
		})(),
		ajeApproved: (() => {
			try { return localStorage.getItem(this.storageKey() + "_approved") === "true"; } catch (e) { return false; }
		})(),
		depreciationRate: 20,
	};

	handlePromptResponse = (stepId, optionId) => {
		this.setState((prev) => {
			let updated = { ...prev.userResponses, [stepId]: optionId };
			try { localStorage.setItem(this.storageKey(), JSON.stringify(updated)); } catch (e) {}
			return { userResponses: updated };
		});
	};

	revertResponse = (stepId) => {
		this.setState((prev) => {
			let updated = { ...prev.userResponses };
			// Clear this step, downstream responses, and any related input keys (e.g., ob_ for opening balances)
			let keysToRemove = Object.keys(updated).filter(function (k) { return k === stepId || k.startsWith(stepId + "_") || k.startsWith("ob_"); });
			for (let k of keysToRemove) delete updated[k];
			try { localStorage.setItem(this.storageKey(), JSON.stringify(updated)); } catch (e) {}
			return { userResponses: updated };
		});
	};

	handleAjeApproved = () => {
		this.update({ ajeApproved: true });
		try { localStorage.setItem(this.storageKey() + "_approved", "true"); } catch (e) {}
	};

	render() {
		let { steps, agentProposals, onApproveAJE, onEditAJEDraft, onApproveContextChange, onApproveAccountMapping, chartOfAccounts, crossCheckFacts } = this.props;

		// Build a draft entry (multi-line shape) from a single-pair cascade
		// proposal at the user's chosen amount, then hand it to the modal so they
		// can edit before approving. The modal owns Save → onApproveAJE; if they
		// close without saving, nothing is persisted.
		const buildDraftFromProposal = (p, amount) => ({
			description: p.description || "",
			source: "human",
			status: "accepted",
			lines: [
				{ account: "", accountDescription: p.debitDescription || null, canonicalCode: p.debitCanonicalCode || null, side: "DEBIT", amount },
				{ account: "", accountDescription: p.creditDescription || null, canonicalCode: p.creditCanonicalCode || null, side: "CREDIT", amount },
			],
		});
		let { showCompleted, userResponses, ajeApproved, depreciationRate } = this.state;

		let resolved = resolveStepsWithUserResponses(steps, userResponses, agentProposals, chartOfAccounts, crossCheckFacts);

		let completedSteps = resolved.filter(function (s) { return s.status === "completed"; });
		let activeSteps = resolved.filter(function (s) { return s.status !== "completed" && s.status !== "not_applicable"; });
		let totalRelevant = resolved.filter(function (s) { return s.status !== "not_applicable"; }).length;

		// If user already approved the AJE, move it to completed
		if (ajeApproved) {
			activeSteps = activeSteps.filter(function (s) { return s.id !== "prepare_depreciation_aje"; });
			let ajeStep = resolved.find(function (s) { return s.id === "prepare_depreciation_aje"; });
			if (ajeStep && !completedSteps.find(function (s) { return s.id === "prepare_depreciation_aje"; })) {
				completedSteps = [...completedSteps, { ...ajeStep, status: "completed", detail: "Depreciation entry added" }];
			}
		}

		// If everything is complete, show a success summary with expandable detail
		if (activeSteps.length === 0 && completedSteps.length > 0) {
			return (
				<div className="afs-review__exec-steps">
					<div className="afs-review__exec-summary afs-review__exec-summary--done" onClick={() => this.update({ showCompleted: !showCompleted })}>
						<Icon.CheckCircle size={14} color="#2e7d32" />
						<span>All {completedSteps.length} steps completed</span>
						{Object.keys(userResponses).length > 0 && (
							<span className="afs-review__exec-change-link" onClick={(e) => {
								e.stopPropagation();
								let userStepIds = completedSteps.filter(function (s) { return userResponses[s.id]; }).map(function (s) { return s.id; });
								if (userStepIds.length > 0) this.revertResponse(userStepIds[userStepIds.length - 1]);
							}}>undo</span>
						)}
						<span className="afs-review__exec-summary-toggle">{showCompleted ? "hide" : "show details"}</span>
					</div>
					{showCompleted && (
						<div className="afs-review__exec-completed-steps">
							{completedSteps.map((step) => {
								return (
									<div key={step.id} className="afs-review__exec-completed-item">
										<Icon.Check size={12} color="#2e7d32" />
										<span>{step.label}</span>
										{step.detail && (step.detail.includes("\n")
											? <div className="afs-review__exec-step-detail">{step.detail.split("\n").map(function (line, i) { return <div key={i}>{i === 0 ? " — " + line : line}</div>; })}</div>
											: <span className="afs-review__exec-step-detail"> — {step.detail}</span>
										)}
										{userResponses[step.id] && (
											<span className="afs-review__exec-change-link" onClick={(e) => { e.stopPropagation(); this.revertResponse(step.id); }}>change</span>
										)}
										<StepTrace trace={step.trace} />
									</div>
								);
							})}
						</div>
					)}
				</div>
			);
		}

		// Only show the first actionable step — the one that needs the user's attention right now.
		// Remaining blocked/waiting steps are hidden to reduce noise.
		let heroStep = activeSteps.length > 0 ? activeSteps[0] : null;
		let remainingCount = activeSteps.length - 1;

		return (
			<div className="afs-review__exec-steps">
				{completedSteps.length > 0 && (
					<>
						<div className="afs-review__exec-summary" onClick={() => this.update({ showCompleted: !showCompleted })}>
							<Icon.CheckCircle size={16} color="#4caf50" />
							<span>{completedSteps.length} of {totalRelevant} steps completed</span>
							{Object.keys(userResponses).length > 0 && (
								<span className="afs-review__exec-change-link" onClick={(e) => {
									e.stopPropagation();
									// Find the last user-driven step and revert it
									let userStepIds = completedSteps.filter(function (s) { return userResponses[s.id]; }).map(function (s) { return s.id; });
									if (userStepIds.length > 0) this.revertResponse(userStepIds[userStepIds.length - 1]);
								}}>undo</span>
							)}
							<span className="afs-review__exec-summary-toggle">{showCompleted ? "hide" : "show"}</span>
						</div>
						{showCompleted && (
							<div className="afs-review__exec-completed-steps">
								{completedSteps.map(function (step) {
									return (
										<div key={step.id} className="afs-review__exec-completed-item">
											<Icon.Check size={12} color="#4caf50" />
											<span>{step.label}</span>
											{step.detail && (step.detail.includes("\n")
											? <div className="afs-review__exec-step-detail">{step.detail.split("\n").map(function (line, i) { return <div key={i}>{i === 0 ? " — " + line : line}</div>; })}</div>
											: <span className="afs-review__exec-step-detail"> — {step.detail}</span>
										)}
											<StepTrace trace={step.trace} />
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
				{heroStep && (() => {
					let step = heroStep;
					let config = getStepStatusConfig(step.status);
					let StepIcon = config.icon;

					return (
						<div className={clsx("afs-review__exec-step", getStepStatusClass(step.status))}>
							<StepIcon size={16} color={config.color} className="afs-review__exec-step-icon" />
							<div className="afs-review__exec-step-content">
								<span className="afs-review__exec-step-label">{step.label}</span>
								{userResponses[step.id] && (
									<span className="afs-review__exec-change-link" onClick={() => this.revertResponse(step.id)}>undo</span>
								)}
								{step.detail && <span className="afs-review__exec-step-detail">{step.detail}</span>}
								<StepTrace trace={step.trace} />
								{step.prompt && step.prompt.options && (
									step.prompt.options.length > 10
										? <SearchableSelect question={step.prompt.question} options={step.prompt.options} onSelect={(optionId) => this.handlePromptResponse(step.id, optionId)} />
										: <ChoicePrompt question={step.prompt.question} options={step.prompt.options} onSelect={(optionId) => this.handlePromptResponse(step.id, optionId)} />
								)}
								{step.prompt && step.prompt.input_fields && (
									<InputPrompt question={step.prompt.question} fields={step.prompt.input_fields} stepId={step.id} onSubmit={(values) => { for (let key in values) this.handlePromptResponse(key, values[key]); }} />
								)}
								{step.prompt && step.prompt.type === "per_item" && step.prompt.items && (
									<div className="afs-review__per-item-decisions">
										<p className="afs-review__per-item-question">{step.prompt.question}</p>
										{step.prompt.items.map((item, itemIdx) => {
											let itemKey = "txn_cap01_item_" + itemIdx;
											let decision = userResponses[itemKey];
											let absAmount = Math.abs(parseFloat(item.amount) || 0);
											let codeParts = (item.canonical_code || "").split(".");
											let shortAccount = codeParts[codeParts.length - 1] || item.canonical_code;
											return (
												<div key={itemIdx} className={clsx("afs-review__per-item-card", decision && "afs-review__per-item-card--resolved")}>
													<div className="afs-review__per-item-header">
														<span className="afs-review__per-item-desc">{item.description}</span>
														<span className="afs-review__per-item-amount">{"$" + absAmount.toLocaleString("en-CA")}</span>
													</div>
													<div className="afs-review__per-item-meta">
														<span>{item.date || "—"}</span>
														<span>{shortAccount.replace(/_/g, " ")}</span>
													</div>
													{decision ? (
														<div className="afs-review__per-item-resolved">
															<Icon.CheckCircle size={14} color="#2e7d32" />
															<span>{decision === "capitalize" ? "Capitalize to PP&E" : "Leave as expense — " + decision.replace(/_/g, " ")}</span>
															<span className="afs-review__exec-change-link" onClick={() => this.revertResponse(itemKey)}>undo</span>
														</div>
													) : (
														<div className="afs-review__per-item-actions">
															<button className="afs-review__per-item-btn afs-review__per-item-btn--capitalize" onClick={() => this.handlePromptResponse(itemKey, "capitalize")}>
																<Icon.ArrowUpCircle size={14} /> Capitalize
															</button>
															<button className="afs-review__per-item-btn afs-review__per-item-btn--expense" onClick={() => this.handlePromptResponse(itemKey, "repair_maintenance")}>
																Repair / Maintenance
															</button>
															<button className="afs-review__per-item-btn afs-review__per-item-btn--expense" onClick={() => this.handlePromptResponse(itemKey, "consumable")}>
																Consumable
															</button>
															<button className="afs-review__per-item-btn afs-review__per-item-btn--expense" onClick={() => this.handlePromptResponse(itemKey, "below_policy_in_context")}>
																Below Policy
															</button>
														</div>
													)}
												</div>
											);
										})}
									</div>
								)}
								{step.inline_proposal && (
									<AJEProposal
										proposal={step.inline_proposal}
										depreciationRate={depreciationRate}
										onRateChange={(v) => this.update({ depreciationRate: v })}
										onApprove={(amount) => {
											if (onApproveAJE) {
												let p = step.inline_proposal;
												onApproveAJE({ description: p.description, debitAccount: "", debitCanonicalCode: p.debitCanonicalCode, creditAccount: "", creditCanonicalCode: p.creditCanonicalCode, amount: amount });
												this.handleAjeApproved();
											}
										}}
										onEdit={onEditAJEDraft ? (amount) => onEditAJEDraft(buildDraftFromProposal(step.inline_proposal, amount)) : null}
									/>
								)}
								{step.inline_proposals && step.inline_proposals.length > 0 && (
									<div>
										{step.inline_proposals.map((proposal, i) => (
											<AJEProposal
												key={i}
												proposal={proposal}
												onApprove={(amount) => {
													if (onApproveAJE) {
														onApproveAJE({ description: proposal.description, debitAccount: "", debitCanonicalCode: proposal.debitCanonicalCode, creditAccount: "", creditCanonicalCode: proposal.creditCanonicalCode, amount: amount });
													}
												}}
												onEdit={onEditAJEDraft ? (amount) => onEditAJEDraft(buildDraftFromProposal(proposal, amount)) : null}
											/>
										))}
									</div>
								)}
								{step.inline_mapping && (
									<MappingProposal mapping={step.inline_mapping} onApprove={function () {
										if (onApproveAccountMapping) onApproveAccountMapping({ account_number: step.inline_mapping.accountNumber, account_name: step.inline_mapping.accountName, canonical_code: step.inline_mapping.canonicalCode });
									}} />
								)}
								{step.inline_context_change && (
									<ContextChangeProposal change={step.inline_context_change} onApprove={function () {
										let changeField = step.inline_context_change.csrsField || step.inline_context_change.field;
										let changeValue = step.inline_context_change.newValue;
										if (onApproveContextChange) {
											onApproveContextChange({ field: changeField, value: changeValue });
										}
									}} />
								)}
								{step.inline_mappings && step.inline_mappings.length > 0 && (
									<div>
										{step.inline_mappings.map(function (mapping, i) {
											return <MappingProposal key={i} mapping={mapping} onApprove={function () {
												if (onApproveAccountMapping) onApproveAccountMapping({ account_number: mapping.accountNumber, account_name: mapping.accountName, canonical_code: mapping.canonicalCode });
											}} />;
										})}
									</div>
								)}
							</div>
						</div>
					);
				})()}
				{remainingCount > 0 && (
					<div className="afs-review__exec-remaining">
						{remainingCount} more step{remainingCount > 1 ? "s" : ""} after this
					</div>
				)}
			</div>
		);
	}
}

export default ExecutionSteps;
